import type { BeckyModel } from '@wix/thunderbolt-becky-root'
import type { DataMapName, RefSchemas } from '@wix/thunderbolt-becky-types'
import type { Catharsis } from '../types/catharsis.types'
import { getDataTypeToRefs } from '@wix/thunderbolt-becky-root'
import _ from 'lodash'

export const singleTransaction = (
	catharsis: Catharsis,
	beckyModel: BeckyModel,
	usedDataMaps: Iterable<DataMapName>,
	runAndReport: <T>(fn: () => T, name: string) => T
) => {
	const {
		structure,
		data,
		shapeStore,
		builderPresetsStore,
		compIdToPathToSelector,
		builderTypeToReducedManifest,
		builderTypeToPresetInfo,
		appDefIdToRefs = {},
		dynamicSchemas,
		...environment
	} = beckyModel
	const reuseDynamicRefSchemas = !!beckyModel.experiments['specs.thunderbolt.reuseDynamicRefSchemas']

	let dataTypeToRefs: Record<string, RefSchemas> = {}

	if (reuseDynamicRefSchemas) {
		// avoid using getDataTypeToRefs with dynamicSchemas as this method is redundant for them
		// however, keep similar logic for builderTypeToRefsByDataType before we refactor svg resolver
		Object.entries(dynamicSchemas).forEach(([mapName, mapRefs]) => {
			Object.entries(mapRefs).forEach(([dataType, refs]) => {
				dataTypeToRefs[dataType] = dynamicSchemas
			})
		})
	} else {
		dataTypeToRefs = getDataTypeToRefs(appDefIdToRefs)
	}

	const builderTypeToRefsByDataType = _.mapValues(
		_.groupBy(Object.keys(dataTypeToRefs), (dataType) => dataType.split('.').slice(0, -1).join('.') || dataType),
		(keys) => _.pick(dataTypeToRefs, keys)
	)

	runAndReport(
		() =>
			catharsis.transaction(
				({ updateStructure, updateData, updateSvgString, updatePresetCss, updateEnvironment, updatePathToSelectorMap, updateBuilderTypeToReducedManifest, updateBuilderTypeToRefsByDataType, updateBuilderTypeToPresetInfo }) => {
					if (reuseDynamicRefSchemas) {
						catharsis.addDynamicSchemas(dynamicSchemas)
					} else {
						Object.values(appDefIdToRefs).forEach((refs) => {
							catharsis.addDynamicSchemas(refs)
						})
					}

					runAndReport(() => {
						Object.entries(builderTypeToRefsByDataType).forEach(([builderType, dataTypeToRefs]) => {
							updateBuilderTypeToRefsByDataType(builderType, dataTypeToRefs)
						})
					}, 'catharsis-updateBuilderTypeToRefsByDataType')

					runAndReport(() => updateEnvironment(environment), 'catharsis-updateEnvironment')

					runAndReport(() => {
						for (const shapeId in shapeStore) {
							updateSvgString(shapeId, shapeStore[shapeId])
						}
					}, 'catharsis-updateSvgString')

					runAndReport(() => {
						for (const widgetId in builderPresetsStore) {
							const widgetPresets = builderPresetsStore[widgetId]
							updatePresetCss(widgetId, widgetPresets)
						}
					}, 'catharsis-updatePresets')

					runAndReport(() => {
						for (const compId in compIdToPathToSelector) {
							const pathToSelector = compIdToPathToSelector[compId]
							updatePathToSelectorMap(compId, pathToSelector)
						}
					}, 'catharsis-updatePathToSelectorMap')

					runAndReport(() => {
						for (const builderType in builderTypeToReducedManifest) {
							const reducedManifest = builderTypeToReducedManifest[builderType]
							if (reducedManifest) {
								updateBuilderTypeToReducedManifest(builderType, reducedManifest)
							}
						}
					}, 'catharsis-updateBuilderTypeToReducedManifest')

					runAndReport(() => {
						for (const builderType in builderTypeToPresetInfo) {
							const presetHashes = builderTypeToPresetInfo[builderType]
							if (presetHashes) {
								updateBuilderTypeToPresetInfo(builderType, presetHashes)
							}
						}
					}, 'catharsis-updateBuilderTypeToPresetInfo')

					runAndReport(() => {
						for (const compId in structure) {
							const comp = structure[compId]
							updateStructure(compId, comp)
						}
					}, 'catharsis-updateStructure')

					runAndReport(() => {
						for (const dataMapName of usedDataMaps) {
							for (const id in data[dataMapName]) {
								const dataItem = data[dataMapName][id]
								updateData(dataMapName, id, dataItem)
							}
						}
					}, 'catharsis-updateData')
				},
				runAndReport
			),
		'catharsis.transaction'
	)
}
